import { buildDenormalizedFormats } from '@aipb/gulliver/catalog/util';
import { DropboxService } from '@aipb/gulliver/dropbox/data-access';
import {
  ImageFormatInfoParams,
  ImageFormatInfoResponse,
  ImageQualityInfoResponse,
  ImageRaw
} from '@aipb/gulliver/dropbox/model';
import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of, tap } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ImagePreviewRepository } from './image-preview.repository';

@Injectable()
export class ImagePreviewService {
  private dropbox = inject(DropboxService);

  constructor(private repository: ImagePreviewRepository) {}

  public init(id: string, lang: string) {
    if (this.repository.hasImage(id) && this.repository.hasImageRaw(id)) {
      this.repository.setActiveId(id);
      return of(this.repository.getImageRaw(id));
    }

    this.repository.setLoading(true);

    return this.dropbox.imageQuality({ res: id, lang }).pipe(
      tap((data) => {
        this.repository.setImage(id, URL.createObjectURL(data[0]));
        this.repository.setImageRaw(id, data);
        this.repository.setActiveId(id);
      }),
      finalize(() => {
        this.repository.setLoading(false);
      })
    );
  }

  public preloadImages(images: string[] = [], lang: string) {
    const requests: Observable<ImageRaw>[] = images.map((imageId) =>
      this.dropbox.imageQuality({ res: imageId, lang }).pipe(
        tap((data) => {
          this.repository.setImage(imageId, URL.createObjectURL(data[0]));
          this.repository.setImageRaw(imageId, data);
        })
      )
    );

    forkJoin(requests).subscribe();
  }

  fetchFormats(params: ImageFormatInfoParams): Observable<ImageFormatInfoResponse> {
    return this.dropbox.imageFormatInfo(params).pipe(
      tap((data) => {
        const formats = buildDenormalizedFormats(data.formats);
        this.repository.setFormats({
          listFormats: {
            formats: formats,
            watermarks: {
              sources: Object.keys(data.watermarks.sources).map((source) => ({
                ...data.watermarks.sources[source],
                id: source
              })),
              sizes: Object.keys(data.watermarks.sizes).map((size) => ({
                ...data.watermarks.sizes[size],
                id: size
              })),
              positions: Object.keys(data.watermarks.positions).map((position) => ({
                ...data.watermarks.positions[position],
                id: position
              }))
            }
          },
          formats: formats
        });
      })
    );
  }

  fetchResolutions(id: string, lang: string): Observable<ImageQualityInfoResponse> {
    return this.dropbox
      .imageQualityInfo({
        res: id,
        lang: lang
      })
      .pipe(
        catchError(() =>
          of({
            resAvailable: [],
            freeDownloadsLeft: 0,
            alreadyDownloaded: false,
            paidDownloadsAllowed: false,
            license: ''
          })
        )
      )
      .pipe(
        tap((data) => {
          this.repository.setResolutions(data);
        })
      );
  }
}
